import { useCallback, useMemo, useState } from 'react';
import { Modal, IconSelector, Icon, Grid, IconButton } from '@ts/storybook';
import { IIcon } from '@ts/ui-models';

interface IconSelectorInputModalProps {
  selectedIcon: IIcon | null;
  disabled?: boolean;
  isViewMode?: boolean;
  withoutClear?: boolean;
  onSelectIcon: (icon: Partial<IIcon> | null) => void;
}

const IconSelectorInputModal = ({
  selectedIcon,
  disabled,
  isViewMode,
  onSelectIcon,
  withoutClear,
}: IconSelectorInputModalProps) => {
  const [showModal, setShowModal] = useState(false);
  const emptyIcon = useMemo(() => ({ name: '', color: '#000000' }), []);
  const [icon, setIcon] = useState<Partial<IIcon>>(selectedIcon ?? emptyIcon);

  const onToggleModal = useCallback(() => {
    setIcon(selectedIcon ?? emptyIcon);
    setShowModal(prev => !prev);
  }, [selectedIcon, emptyIcon]);

  const onClose = useCallback(() => {
    setShowModal(false);
  }, []);

  const onConfirmIcon = useCallback(() => {
    onSelectIcon(icon.name ? { name: icon.name, color: icon.color } : emptyIcon);
    setShowModal(false);
  }, [icon, onSelectIcon, emptyIcon]);

  const onRemoveIcon = useCallback(() => {
    setIcon(emptyIcon);
    onSelectIcon(null);
  }, [onSelectIcon, emptyIcon]);

  return (
    <Grid container size={'auto'}>
      <Modal
        withHeader
        title="Icon selector"
        maxWidth="md"
        fullWidth
        labelOk="Confirm"
        labelCancel="Cancel"
        open={showModal}
        onSubmit={onConfirmIcon}
        onClose={onClose}
        disabled={!icon.name}
      >
        <IconSelector onChangeIcon={setIcon} defaultIcon={icon} />
      </Modal>
      <Grid container size={'auto'} direction={'row'} alignItems="center">
        {selectedIcon ? (
          <Icon size={32} icon={selectedIcon.name} color={selectedIcon.color} />
        ) : (
          <span className="w-[45px] h-[30px] border-b border-black" />
        )}
        {!isViewMode && (
          <>
            <IconButton icon="search" onClick={onToggleModal} disabled={disabled} />
            {!withoutClear && <IconButton icon="clear" onClick={onRemoveIcon} disabled={disabled} />}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default IconSelectorInputModal;
